export type HubTable = {
  caption?: string;
  head: string[];
  rows: string[][];
};

export type HubSection = {
  h2: string;
  paragraphs: string[];
  bullets?: string[];
  table?: HubTable;
};

export type HubFaq = { q: string; a: string };

function TableBlock({ table }: { table: HubTable }) {
  return (
    <figure className="mt-6 overflow-x-auto rounded-2xl border border-border">
      <table className="w-full min-w-[420px] border-collapse text-left text-sm">
        {table.caption && (
          <caption className="border-b border-border bg-warm px-4 py-3 text-left text-xs uppercase tracking-wide text-muted-foreground">
            {table.caption}
          </caption>
        )}
        <thead>
          <tr className="bg-warm/60">
            {table.head.map((h) => (
              <th key={h} scope="col" className="border-b border-border px-4 py-3 font-medium">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {table.rows.map((r) => (
            <tr key={r.join("|")} className="border-b border-border/70 last:border-0">
              {r.map((cell, i) => (
                <td
                  key={i}
                  className={
                    i === 0
                      ? "px-4 py-3 font-medium text-foreground"
                      : "px-4 py-3 text-muted-foreground"
                  }
                >
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </figure>
  );
}

export function HubEditorial({
  eyebrow,
  sections,
  faq,
  faqTitle = "Vanliga frågor",
  tldr,
  tldrTitle = "Kort svar",
  updated,
  author,
}: {
  eyebrow?: string;
  sections: HubSection[];
  faq?: HubFaq[];
  faqTitle?: string;
  /** Direktsvar högst upp — det AI-sökmotorer och Googles AI Overviews citerar. */
  tldr?: string[];
  tldrTitle?: string;
  updated?: string;
  author?: string;
}) {
  return (
    <section className="container-edge pb-20 md:pb-24">
      {eyebrow && <p className="eyebrow">{eyebrow}</p>}

      {tldr && tldr.length > 0 && (
        <div className="mt-6 rounded-3xl border border-accent/30 bg-warm p-7 md:p-8">
          <h2 className="font-display text-2xl leading-tight md:text-3xl">{tldrTitle}</h2>
          <ul className="mt-5 space-y-3">
            {tldr.map((t) => (
              <li key={t} className="flex gap-3 text-sm leading-relaxed text-foreground/90 md:text-[15px]">
                <span aria-hidden className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-accent" />
                <span>{t}</span>
              </li>
            ))}
          </ul>
          {(updated || author) && (
            <p className="mt-6 text-xs text-muted-foreground">
              {author && <>Granskad av {author}</>}
              {author && updated && " · "}
              {updated && <>Uppdaterad {updated}</>}
            </p>
          )}
        </div>
      )}

      <div className="mt-6 grid gap-8 md:grid-cols-2">
        {sections.map((s) => (
          <article key={s.h2} className="rounded-3xl border border-border bg-card p-7 md:p-8">
            <h2 className="font-display text-2xl leading-tight md:text-3xl">{s.h2}</h2>
            {s.paragraphs.map((p, i) => (
              <p key={i} className="mt-4 text-sm leading-relaxed text-muted-foreground md:text-[15px]">
                {p}
              </p>
            ))}
            {s.bullets && s.bullets.length > 0 && (
              <ul className="mt-5 space-y-2.5">
                {s.bullets.map((b) => (
                  <li key={b} className="flex gap-3 text-sm leading-relaxed text-foreground/85">
                    <span aria-hidden className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-accent" />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            )}
            {s.table && <TableBlock table={s.table} />}
          </article>
        ))}
      </div>

      {faq && faq.length > 0 && (
        <div className="mt-12">
          <h2 className="font-display text-3xl md:text-4xl">{faqTitle}</h2>
          <dl className="mt-6 divide-y divide-border overflow-hidden rounded-3xl border border-border bg-card">
            {faq.map((f) => (
              <div key={f.q} className="p-6 md:p-7">
                <dt className="font-display text-lg md:text-xl">{f.q}</dt>
                <dd className="mt-2 text-sm leading-relaxed text-muted-foreground md:text-[15px]">{f.a}</dd>
              </div>
            ))}
          </dl>
        </div>
      )}
    </section>
  );
}

export function faqSchema(faq: HubFaq[]) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faq.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };
}
